import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, Chip, IconButton, Typography,
  CircularProgress, Alert, InputAdornment, FormControl, InputLabel,
  TablePagination, Tooltip, Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import fieldRegistryService, { FieldRegistryEntry, FieldRegistryStats } from '../../services/fieldRegistryService';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  R: 'success',
  D: 'warning',
  O: 'info',
  N: 'default',
};

const FieldRegistryTable: React.FC = () => {
  const [fields, setFields] = useState<FieldRegistryEntry[]>([]);
  const [stats, setStats] = useState<FieldRegistryStats | null>(null);
  const [sections, setSections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<FieldRegistryEntry>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [fieldsRes, statsRes, sectionsRes] = await Promise.all([
        fieldRegistryService.getAll(undefined, false),
        fieldRegistryService.getStats(),
        fieldRegistryService.getSections(),
      ]);
      setFields(fieldsRes.fields);
      setStats(statsRes);
      setSections(sectionsRes);
    } catch (err: any) {
      setError(err.message || 'Failed to load field registry');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return fields.filter(f => {
      const matchesSearch = !search ||
        f.field_key.toLowerCase().includes(search.toLowerCase()) ||
        (f.label_en || '').toLowerCase().includes(search.toLowerCase()) ||
        (f.ml_predictor_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (f.codelist_id || '').toLowerCase().includes(search.toLowerCase());
      const matchesSection = !sectionFilter || f.section === sectionFilter;
      return matchesSearch && matchesSection;
    });
  }, [fields, search, sectionFilter]);

  const startEdit = (field: FieldRegistryEntry) => {
    setEditingKey(field.field_key);
    setEditValues({ label_en: field.label_en, codelist_id: field.codelist_id, description: field.description, is_active: field.is_active });
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    try {
      const updated = await fieldRegistryService.updateField(editingKey, editValues);
      setFields(prev => prev.map(f => f.field_key === editingKey ? { ...f, ...updated } : f));
      setEditingKey(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  };

  const handleExport = async () => {
    const data = await fieldRegistryService.exportAll();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atlas_field_registry.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${stats.total}`} />
          <Chip label={`Active: ${stats.active}`} color="success" />
          <Chip label={`With Codelist: ${stats.with_codelist}`} color="info" />
          <Chip label={`With ML Mapping: ${stats.with_ml_mapping}`} color="warning" />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search fields..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Section</InputLabel>
          <Select value={sectionFilter} onChange={e => { setSectionFilter(e.target.value as string); setPage(0); }} label="Section">
            <MenuItem value="">All</MenuItem>
            {sections.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Export as JSON">
          <IconButton onClick={handleExport}><DownloadIcon /></IconButton>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filtered.length} fields
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Field Key</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Codelist</TableCell>
              <TableCell>ML Name</TableCell>
              <TableCell>Format</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(f => (
              <TableRow key={f.field_key} sx={{ opacity: f.is_active ? 1 : 0.5 }}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.field_key}</TableCell>
                <TableCell><Chip label={f.section} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  {editingKey === f.field_key ? (
                    <TextField size="small" value={editValues.label_en || ''} onChange={e => setEditValues(p => ({ ...p, label_en: e.target.value }))} />
                  ) : (f.label_en || '—')}
                </TableCell>
                <TableCell><Chip label={f.required_status} size="small" color={STATUS_COLORS[f.required_status] || 'default'} /></TableCell>
                <TableCell>
                  {editingKey === f.field_key ? (
                    <TextField size="small" value={editValues.codelist_id || ''} onChange={e => setEditValues(p => ({ ...p, codelist_id: e.target.value }))} sx={{ width: 80 }} />
                  ) : (f.codelist_id || '—')}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{f.ml_predictor_name || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{f.format || '—'}</TableCell>
                <TableCell>
                  {editingKey === f.field_key ? (
                    <Switch checked={editValues.is_active ?? f.is_active} onChange={e => setEditValues(p => ({ ...p, is_active: e.target.checked }))} size="small" />
                  ) : (f.is_active ? '✓' : '✗')}
                </TableCell>
                <TableCell>
                  {editingKey === f.field_key ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={saveEdit}><SaveIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setEditingKey(null)}><CancelIcon fontSize="small" /></IconButton>
                    </Box>
                  ) : (
                    <IconButton size="small" onClick={() => startEdit(f)}><EditIcon fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div" count={filtered.length} page={page}
        rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
};

export default FieldRegistryTable;
